import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PrMatrixTable, PrLegend, prThemeVars } from '../../components/progress/PrStatus';

const API = process.env.REACT_APP_API || '/api';

// بوابة العرض العامة لتقرير التقدّم — بلا تسجيل دخول:
// أشهر منشورة (لقطات مجمّدة) + تبويب المتابعة اللحظية إن فُعّل
export default function ProgressPortal() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = useCallback(async (pwd, month) => {
    setLoading(true);
    setError('');
    try {
      const qs = month ? `?month=${month}` : '';
      // كلمة المرور تُرسل في الجسم لا في الهيدر — الهيدر لا يقبل العربية
      const res = await fetch(`${API}/progress/portal/${slug}${qs}`, pwd ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ password: pwd }),
      } : { headers: { Accept: 'application/json' } });
      const json = await res.json();
      if (res.status === 401 && json.password_required) {
        setNeedsPassword(true);
        setMeta(json.report);
        if (pwd) setError('كلمة المرور غير صحيحة');
        setData(null);
      } else if (!res.ok) {
        setError(json.error || 'تعذّر تحميل التقرير');
        setData(null);
      } else {
        setData(json);
        setNeedsPassword(false);
      }
    } catch {
      setError('فشل تحميل الصفحة');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    const saved = sessionStorage.getItem(`progress-portal-pwd-${slug}`);
    fetchReport(saved || null, searchParams.get('month') || null);
  }, [fetchReport, slug]);

  const submitPassword = (e) => {
    e.preventDefault();
    sessionStorage.setItem(`progress-portal-pwd-${slug}`, password);
    fetchReport(password, searchParams.get('month') || null);
  };

  const switchMonth = (m) => {
    const pwd = sessionStorage.getItem(`progress-portal-pwd-${slug}`);
    setSearchParams(m ? { month: m } : {}, { replace: true });
    fetchReport(pwd || null, m);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const pwd = sessionStorage.getItem(`progress-portal-pwd-${slug}`);
      const m = data?.mode === 'live' ? 'live' : data?.month;
      const qs = m ? `?month=${m}` : '';
      const res = await fetch(`${API}/progress/portal/${slug}/pdf${qs}`, pwd ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/pdf' },
        body: JSON.stringify({ password: pwd }),
      } : { headers: { Accept: 'application/pdf' } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `تقرير-${data?.payload?.report?.donor_name || ''}${data?.month ? '-' + data.month : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('تعذّر توليد ملف PDF، حاول مجدداً');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div style={wrap}><div className="loader">جاري التحميل...</div></div>;

  if (needsPassword) {
    return (
      <div style={wrap} dir="rtl">
        <form onSubmit={submitPassword} className="card" style={{ maxWidth: 360, margin: '60px auto', textAlign: 'center' }}>
          {meta?.logo_path && <img src={meta.logo_path} alt="" style={{ maxHeight: 60, marginBottom: 12 }} />}
          <div className="card-title" style={{ marginBottom: 12 }}>{meta?.donor_name || meta?.title || 'تقرير التقدّم'}</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>هذه الصفحة محمية بكلمة مرور.</p>
          <input className="form-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="كلمة المرور" style={{ margin: '10px 0' }} />
          {error && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
          <button className="btn btn-primary" type="submit" style={{ width: '100%' }}>دخول</button>
        </form>
      </div>
    );
  }

  if (error) return <div style={wrap}><div className="empty">{error}</div></div>;
  if (!data) return <div style={wrap}><div className="empty">لا يوجد تقرير منشور</div></div>;

  const { payload, months, mode, month, month_label: monthLabel, version, live_enabled: liveEnabled } = data;
  const summaryLines = (payload.summary || '').split('\n').filter((l) => l.trim());
  const alertLines = (payload.alerts || '').split('\n').filter((l) => l.trim());
  // اللقطات القديمة (قبل schema 2) بلا مجموعات — ملاحظاتها تُعرض كما جُمّدت
  const newSchema = (payload.schema_version || 1) >= 2;

  // أقسام المشروع الموحّد تتجمع بمعرّف المجموعة لا بالتجاور — التبعثر في الترتيب
  // كان يكرر الترويسة الجامعة وملاحظاتها
  const sections = [];
  const byGroup = new Map();
  payload.projects.forEach((p) => {
    const g = p.group || null;
    if (!g) { sections.push({ group: null, items: [p] }); return; }
    const existing = byGroup.get(g.id);
    if (existing) { existing.items.push(p); return; }
    const sec = { group: g, items: [p] };
    byGroup.set(g.id, sec);
    sections.push(sec);
  });

  return (
    <div className="progress-portal" style={{ ...wrap, ...prThemeVars(payload.report) }} dir="rtl">
      {/* الغلاف */}
      <div className="report-cover" style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        <div className="report-cover-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div className="report-cover-text">
            <div className="report-cover-kicker">{payload.report.donor_name}</div>
            <h1 className="report-cover-title">{payload.report.title}</h1>
            <div className="report-cover-period">
              {mode === 'live' ? '⚡ متابعة لحظية — الحالة الآن' : monthLabel}
              {mode === 'published' && version > 1 && (
                <span style={{ marginInlineStart: 8, fontSize: 12, opacity: 0.85 }}>نسخة منقحة — الإصدار {version}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {payload.report.logo_path && <img src={payload.report.logo_path} alt="" style={{ maxHeight: 64, background: '#fff', borderRadius: 8, padding: 4 }} />}
            {payload.report.logo2_path && <img src={payload.report.logo2_path} alt="" style={{ maxHeight: 64, background: '#fff', borderRadius: 8, padding: 4 }} />}
          </div>
        </div>
      </div>

      {/* تبويبات الأشهر + اللحظي */}
      {(months.length > 0 || liveEnabled) && (
        <div className="card no-print" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {months.map((m) => (
            <button key={m.report_month}
              className={`btn btn-sm ${mode === 'published' && m.report_month === month ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => switchMonth(m.report_month)}>
              {m.label}
            </button>
          ))}
          {liveEnabled && (
            <button className={`btn btn-sm ${mode === 'live' ? 'btn-primary' : 'btn-outline'}`} onClick={() => switchMonth('live')}>
              ⚡ المتابعة اللحظية
            </button>
          )}
          <button className="btn btn-sm btn-primary" style={{ marginInlineStart: 'auto' }} onClick={downloadPdf} disabled={downloading}>
            {downloading ? '⏳ جارٍ التوليد...' : '⬇ تحميل PDF'}
          </button>
          <button className="btn btn-sm btn-outline" onClick={() => window.print()}>🖨 طباعة</button>
        </div>
      )}
      {mode === 'live' && (
        <div className="no-print" style={{ marginBottom: 16, fontSize: 12.5, background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '8px 12px' }}>
          ⚡ المتابعة اللحظية تعرض الحالة الآن، وليس لقطة شهر منشورة — للاطلاع على تقرير شهر معتمد اختر الشهر من الأزرار أعلاه.
        </div>
      )}

      {/* محتويات التقرير */}
      {payload.projects.length > 1 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="report-table-title">محتويات التقرير</div>
          <div className="table-wrap">
            <table className="report-matrix">
              <thead><tr><th style={{ width: 50 }}>م</th><th className="report-matrix-rowhead">البيــان</th></tr></thead>
              <tbody>
                {sections.map((sec, i) => sec.group ? (
                  <React.Fragment key={i}>
                    <tr><td>{i + 1}</td><td className="report-matrix-rowhead">{sec.group.title}</td></tr>
                    {sec.items.map((p, j) => (
                      <tr key={j}><td /><td style={{ textAlign: 'right', paddingInlineStart: 32, fontSize: 12, color: '#4b5563' }}>{p.title}{p.sheikh_name ? ` — ${p.sheikh_name}` : ''}</td></tr>
                    ))}
                  </React.Fragment>
                ) : (
                  <tr key={i}><td>{i + 1}</td><td className="report-matrix-rowhead">{sec.items[0].title}{sec.items[0].sheikh_name ? ` — ${sec.items[0].sheikh_name}` : ''}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* المشاريع — المجمّعة تحت مشروع موحّد تُعرض داخل قسم جامع واحد */}
      {sections.map((sec, i) => sec.group ? (
        <div key={i} className="report-section">
          <div className="report-section-header" style={sec.group.color ? { background: sec.group.color } : undefined}>
            <h3>{sec.group.title}</h3>
          </div>
          {sec.items.map((p, j) => (
            <div key={j} className="report-group-member">
              <div className="report-member-header" style={p.color ? { color: p.color, borderInlineStartColor: p.color } : undefined}>
                {p.title}{p.sheikh_name ? ` — ${p.sheikh_name}` : ''}
              </div>
              {p.matrices.length === 0
                ? <div className="report-empty">لا بيانات لهذا القسم بعد</div>
                : p.matrices.map((m, k) => <PrMatrixTable key={k} matrix={m} sectionColor={p.color || sec.group.color} />)}
              {p.notes && <div style={{ padding: '4px 18px 10px', fontSize: 13, whiteSpace: 'pre-line' }}><strong>ملاحظة:</strong> {p.notes}</div>}
            </div>
          ))}
          {sec.group.notes && (
            <div style={{ margin: '10px 18px 14px', padding: '10px 12px', fontSize: 13, whiteSpace: 'pre-line', background: '#f9fafb', borderRadius: 8, border: '1px dashed #d1d5db' }}>
              <strong>ملاحظات المشروع:</strong> {sec.group.notes}
            </div>
          )}
        </div>
      ) : (
        sec.items.map((p) => (
          <div key={i} className="report-section">
            <div className="report-section-header" style={p.color ? { background: p.color } : undefined}>
              <h3>{p.title}</h3>
              {p.sheikh_name && <div className="report-section-stats">{p.sheikh_name}</div>}
            </div>
            {p.matrices.length === 0
              ? <div className="report-empty">لا بيانات لهذا المشروع بعد</div>
              : p.matrices.map((m, j) => <PrMatrixTable key={j} matrix={m} sectionColor={p.color} />)}
            {p.notes && <div style={{ padding: '4px 18px 14px', fontSize: 13, ...(newSchema ? { whiteSpace: 'pre-line' } : {}) }}><strong>ملاحظة:</strong> {p.notes}</div>}
          </div>
        ))
      ))}

      <PrLegend />

      {/* الخلاصة */}
      {summaryLines.length > 0 && (
        <div className="report-section">
          <div className="report-section-header"><h3>خلاصة التقرير</h3></div>
          <ol style={{ margin: 0, padding: '14px 32px', lineHeight: 2 }}>
            {summaryLines.map((l, i) => <li key={i}>{l}</li>)}
          </ol>
        </div>
      )}

      {/* التنبيهات */}
      {alertLines.length > 0 && (
        <div className="report-section">
          <div className="report-section-header" style={{ background: 'var(--c-coral)' }}><h3>التنبيهات</h3></div>
          <ol style={{ margin: 0, padding: '14px 32px', lineHeight: 2 }}>
            {alertLines.map((l, i) => <li key={i}>{l}</li>)}
          </ol>
        </div>
      )}

      <div style={{ textAlign: 'center', color: 'var(--report-brand)', fontWeight: 700, padding: '18px 0 30px' }}>
        نهاية التقرير
        {mode === 'live' && payload.generated_at && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginTop: 6 }}>
            آخر تحديث: {new Date(payload.generated_at).toLocaleString('ar')}
          </div>
        )}
      </div>
    </div>
  );
}

const wrap = { maxWidth: 1000, margin: '0 auto', padding: 20 };
